import {
  ArrowUp,
  History,
  Loader2,
  Square,
  SquarePen,
  Trash2,
  X,
} from 'lucide-react';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAssistant } from './assistant-context';
import {
  anomalyAnswerSections,
  assistantUserMessageLabel,
  isGeneratedAnomalyPrompt,
} from './assistant-message-format';

const messageText = (parts: ReadonlyArray<{ type: string }>): string =>
  parts
    .filter(
      (part): part is { type: 'text'; text: string } => part.type === 'text',
    )
    .map((part) => part.text)
    .join('\n');

const suggestions = [
  'What changed this week?',
  'Where are conversions coming from?',
  'What should I investigate next?',
];

const markdownClassName =
  'max-w-none text-sm leading-6 text-ink [&_a]:text-accent [&_a]:underline [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5';

const AssistantMarkdown = ({ text }: { text: string }) => (
  <div className={markdownClassName}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

const AnomalyAnswer = ({ text }: { text: string }) => {
  const sections = anomalyAnswerSections(text);
  return (
    <div>
      <AssistantMarkdown text={sections.conclusion} />
      {sections.confidence ? (
        <p className="mt-2 text-xs text-ink-muted">
          <span className="font-medium text-ink">Confidence:</span>{' '}
          {sections.confidence}
        </p>
      ) : null}
      {sections.evidence ? (
        <details className="group mt-3 border-t border-border pt-2">
          <summary className="min-h-8 cursor-pointer list-none text-xs font-medium text-ink-muted outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-accent/30 [&::-webkit-details-marker]:hidden">
            <span className="group-open:hidden">View evidence</span>
            <span className="hidden group-open:inline">Hide evidence</span>
          </summary>
          <div className="pb-1">
            <AssistantMarkdown text={sections.evidence} />
          </div>
        </details>
      ) : null}
    </div>
  );
};

export const AssistantPanel = () => {
  const assistant = useAssistant();
  const [draft, setDraft] = React.useState('');
  const endRef = React.useRef<HTMLDivElement | null>(null);
  const busy =
    assistant.status === 'submitted' || assistant.status === 'streaming';

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [assistant.messages, assistant.status]);

  const submit = async (text = draft) => {
    const value = text.trim();
    if (!value || busy) {
      return;
    }
    setDraft('');
    await assistant.send(value).catch(() => setDraft(value));
  };

  return (
    <aside
      id="gainsignal-assistant"
      className={cn(
        'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-surface shadow-2xl transition-transform duration-200 ease-out sm:w-[26rem]',
        assistant.isOpen
          ? 'translate-x-0'
          : 'pointer-events-none translate-x-full',
      )}
      aria-label="GainSignal AI"
      aria-hidden={!assistant.isOpen}
      inert={!assistant.isOpen}
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
        <div className="flex size-7 items-center justify-center rounded-lg bg-accent-subtle text-accent">
          <span className="text-sm">✦</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">GainSignal AI</p>
          <p className="text-[11px] text-ink-subtle">
            Read-only analytics copilot
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={assistant.newChat}
            aria-label="New conversation"
          >
            <SquarePen />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Conversation history"
              >
                <History />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Conversations</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {assistant.sessions.length === 0 ? (
                <p className="px-2 py-3 text-xs text-ink-subtle">
                  No previous conversations
                </p>
              ) : (
                assistant.sessions.map((session) => (
                  <DropdownMenuItem
                    key={session.id}
                    onSelect={() => void assistant.selectSession(session.id)}
                    className={cn(
                      'group',
                      session.id === assistant.sessionId && 'bg-surface-muted',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {session.title}
                    </span>
                    <button
                      type="button"
                      className="rounded p-1 text-ink-subtle opacity-0 hover:text-critical group-hover:opacity-100"
                      onClick={(event) => {
                        event.stopPropagation();
                        void assistant.deleteSession(session.id);
                      }}
                      aria-label={`Delete ${session.title}`}
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="icon"
            onClick={assistant.close}
            aria-label="Close assistant"
          >
            <X />
          </Button>
        </div>
      </header>

      {Object.keys(assistant.pageContext).length ? (
        <div className="border-b border-border px-4 py-2 text-xs text-ink-subtle">
          Viewing{' '}
          <span className="font-medium text-ink-muted">
            {assistant.pageContext.projectName ?? 'this project'} ·{' '}
            {assistant.pageContext.page ?? 'dashboard'}
          </span>
          {assistant.pageContext.rangeLabel
            ? ` · ${assistant.pageContext.rangeLabel}`
            : ''}
        </div>
      ) : null}

      <div
        className="min-h-0 flex-1 overflow-y-auto px-4 py-5"
        aria-live="polite"
      >
        {assistant.isLoadingHistory ? (
          <div className="flex h-full items-center justify-center text-ink-subtle">
            <Loader2 className="animate-spin" />
          </div>
        ) : assistant.messages.length === 0 ? (
          <div className="flex min-h-full flex-col justify-end pb-2">
            <div className="mb-auto pt-8">
              <h2 className="text-lg font-semibold tracking-tight text-ink">
                What do you want to understand?
              </h2>
              <p className="mt-1 max-w-xs text-sm text-ink-muted">
                Ask about the project you are viewing. I’ll check the underlying
                analytics before answering.
              </p>
            </div>
            <div className="space-y-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="block w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-left text-sm text-ink transition-colors hover:bg-surface-muted"
                  onClick={() => void submit(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {assistant.messages.map((message, messageIndex) => {
              const text = messageText(message.parts);
              if (!text) {
                return null;
              }
              const previousMessage = assistant.messages[messageIndex - 1];
              const previousText = previousMessage
                ? messageText(previousMessage.parts)
                : '';
              const anomalyResponse =
                message.role === 'assistant' &&
                previousMessage?.role === 'user' &&
                isGeneratedAnomalyPrompt(previousText);
              return message.role === 'user' ? (
                <div
                  key={message.id}
                  className="ml-10 rounded-2xl rounded-br-md bg-surface-muted px-3.5 py-2.5 text-sm text-ink"
                >
                  {assistantUserMessageLabel(text)}
                </div>
              ) : (
                <React.Fragment key={message.id}>
                  {anomalyResponse ? (
                    <AnomalyAnswer text={text} />
                  ) : (
                    <AssistantMarkdown text={text} />
                  )}
                </React.Fragment>
              );
            })}
            {assistant.status === 'submitted' ? (
              <div className="flex items-center gap-2 text-xs text-ink-subtle">
                <Loader2 className="size-3.5 animate-spin" />
                Checking your data…
              </div>
            ) : null}
            {assistant.error ? (
              <p className="rounded-lg bg-critical-subtle px-3 py-2 text-sm text-critical">
                {assistant.error.message ||
                  'The assistant could not complete that request.'}
              </p>
            ) : null}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form
        className="shrink-0 border-t border-border p-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="rounded-xl border border-border-strong bg-canvas p-2 focus-within:ring-2 focus-within:ring-accent/25">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void submit();
              }
            }}
            rows={3}
            maxLength={6000}
            placeholder="Ask about this project…"
            className="block w-full resize-none bg-transparent px-1.5 py-1 text-sm text-ink outline-none placeholder:text-ink-subtle"
          />
          <div className="flex items-center justify-between pl-1.5">
            <span className="text-[11px] text-ink-subtle">Enter to send</span>
            {busy ? (
              <Button
                type="button"
                size="icon"
                variant="secondary"
                onClick={assistant.stop}
                aria-label="Stop response"
              >
                <Square className="fill-current" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!draft.trim()}
                aria-label="Send message"
              >
                <ArrowUp />
              </Button>
            )}
          </div>
        </div>
      </form>
    </aside>
  );
};
